import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchCategories,
  createCategory,
  updateCategory,
  toggleCategoryActiveState,
} from "@/data/mockData";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Save,
  ImagePlus,
  Eye,
  EyeOff,
  MoveVertical,
} from "lucide-react";
import { z } from "zod";
import { Switch } from "@/components/ui/switch";
import MediaPicker from "@/components/ui/media-picker";
import { useToast } from "@/components/ui/use-toast";
import { Category } from "@/types";

// Form validation schema
const categorySchema = z.object({
  name: z
    .string()
    .min(2, { message: "Category name must be at least 2 characters" })
    .max(50, { message: "Category name must be less than 50 characters" }),
  name_mz: z
    .string()
    .min(2, { message: "Mizo name must be at least 2 characters" })
    .max(50, { message: "Mizo name must be less than 50 characters" })
    .optional()
    .or(z.literal("")),
  iconUrl: z.string().optional(),
  position: z.number().optional(),
  isActive: z.boolean(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

const CategoryForm = () => {
  const { id } = useParams();
  const idAsNumber = id ? parseInt(id) : undefined;
  const navigate = useNavigate();
  const isEditMode = !!id;
  const queryClient = useQueryClient();

  // Form state
  const [formData, setFormData] = useState<CategoryFormData>({
    name: "",
    name_mz: "",
    iconUrl: "",
    position: undefined,
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Fetch categories to find the one being edited
  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  // If editing, populate the form with the category data
  useEffect(() => {
    if (isEditMode && categories && idAsNumber) {
      const category = categories.find((cat) => cat.id === idAsNumber);
      if (category) {
        setFormData({
          name: category.name,
          name_mz: category.name_mz || "",
          iconUrl: category.icon || "",
          position: category.position,
          isActive: category.isActive,
        });
      } else {
        // Category not found
        navigate("/admin/categories", { replace: true });
      }
    } else if (!isEditMode && categories) {
      // For new categories, set the position to the highest + 1
      const highestPosition = Math.max(
        ...categories.map((c) => c.position ?? 0),
        0
      );
      setFormData((prev) => ({
        ...prev,
        position: highestPosition + 1,
      }));
    }
  }, [isEditMode, id, idAsNumber, categories, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "position") {
      // Convert position to number if it's valid, otherwise set to undefined
      const numValue = parseInt(value);
      setFormData((prev) => ({
        ...prev,
        [name]: isNaN(numValue) ? undefined : numValue,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear the error for this field when the user types
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleToggleActive = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, isActive: checked }));
  };

  const handleIconSelect = (url: string) => {
    setFormData((prev) => ({ ...prev, iconUrl: url }));
  };

  const validateForm = () => {
    try {
      categorySchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // In a real app, you would call an API to save the category
      if (isEditMode && idAsNumber) {
        await updateCategory(idAsNumber, {
          name: formData.name,
          name_mz: formData.name_mz || undefined,
          icon: formData.iconUrl,
          position: formData.position,
          is_active: formData.isActive,
        });

        // Update active state if needed
        const existingCategory = categories?.find(
          (cat) => cat.id === idAsNumber
        );
        if (
          existingCategory &&
          existingCategory.isActive !== formData.isActive
        ) {
          await toggleCategoryActiveState(idAsNumber, formData.isActive);
        }

        toast({
          title: "Category updated",
          description: `"${formData.name}" has been updated successfully.`,
        });
      } else {
        await createCategory(
          formData.name,
          formData.name_mz || undefined,
          formData.iconUrl,
          formData.position
        );
        toast({
          title: "Category created",
          description: `"${formData.name}" has been created successfully.`,
        });
      }

      // Invalidate categories query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ["categories"] });

      navigate("/admin/categories");
    } catch (error) {
      console.error("Error saving category:", error);
      toast({
        title: "Error",
        description: "An error occurred while saving the category",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && isEditMode) {
    return (
      <AdminLayout title={isEditMode ? "Edit Category" : "New Category"}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  // Calculate the highest current position for setting a default
  const highestPosition = categories
    ? Math.max(...categories.map((c) => c.position ?? 0))
    : 0;

  return (
    <AdminLayout title={isEditMode ? "Edit Category" : "New Category"}>
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Categories
        </Button>
      </div>

      <Card className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>
              {isEditMode ? "Edit Category" : "Create New Category"}
            </CardTitle>
            <CardDescription>
              {isEditMode
                ? "Update the category information below"
                : "Fill out the form below to create a new category"}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className={errors.name ? "text-destructive" : ""}
              >
                Category Name
              </Label>
              <Input
                id="name"
                name="name"
                placeholder="Enter category name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? "border-destructive" : ""}
              />
              {errors.name && (
                <p className="text-destructive text-sm">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="name_mz"
                className={errors.name_mz ? "text-destructive" : ""}
              >
                Mizo Name
              </Label>
              <Input
                id="name_mz"
                name="name_mz"
                placeholder="Enter Mizo name"
                value={formData.name_mz}
                onChange={handleChange}
                className={errors.name_mz ? "border-destructive" : ""}
              />
              {errors.name_mz && (
                <p className="text-destructive text-sm">{errors.name_mz}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="position" className="flex items-center gap-2">
                <MoveVertical className="h-4 w-4" />
                Display Position
              </Label>
              <Input
                id="position"
                name="position"
                type="number"
                min="1"
                placeholder={isEditMode ? "Current position" : "Enter position"}
                value={formData.position === undefined ? "" : formData.position}
                onChange={handleChange}
              />
              <p className="text-sm text-muted-foreground">
                Lower numbers appear first. Leave empty for automatic
                positioning.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="is-active" className="flex items-center gap-2">
                Visibility Status
              </Label>
              <div className="flex items-center space-x-4 border rounded-md p-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="is-active"
                    checked={formData.isActive}
                    onCheckedChange={handleToggleActive}
                  />
                  <Label htmlFor="is-active" className="cursor-pointer">
                    {formData.isActive ? "Active (Visible)" : "Hidden"}
                  </Label>
                </div>
                <div className="text-muted-foreground text-sm flex items-center ml-2">
                  {formData.isActive ? (
                    <>
                      <Eye className="h-4 w-4 mr-2 text-green-500" />
                      <span>This category will be visible to users</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      <span>This category will be hidden from users</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ImagePlus className="h-4 w-4" />
                Category Icon
              </Label>
              <MediaPicker
                onSelect={handleIconSelect}
                currentValue={formData.iconUrl}
                allowedTypes={["image/jpeg", "image/jpg", "image/png"]}
                title="Select Category Icon"
                description="Choose or upload an image to use as your category icon"
                buttonText="Select or Upload Icon"
                showPreview={true}
              />
            </div>
          </CardContent>

          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isEditMode ? "Update Category" : "Create Category"}
                </>
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AdminLayout>
  );
};

export default CategoryForm;
